import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, formatValue } from "@/lib/api";
import PublicShell from "@/components/PublicShell";
import { PosRank } from "@/components/PlayerHeadshot";
import DmeTag from "@/components/DmeTag";
import { Loader2 } from "lucide-react";

const FORMATS = { "1qb": "1QB", superflex: "Superflex", tep: "TE Premium" };
const POSITIONS = { all: "Overall", qb: "QB", rb: "RB", wr: "WR", te: "TE" };
const SEASON = new Date().getFullYear();

export default function PublicPositionRankings() {
  const { format: fParam, position: pParam } = useParams();
  const nav = useNavigate();
  const fmt = FORMATS[fParam] ? fParam : "superflex";
  const pos = POSITIONS[pParam] ? pParam : "all";
  const [rows, setRows] = useState(null);

  useEffect(() => {
    setRows(null);
    api.get("/public/players", { params: { format: fmt, position: pos === "all" ? undefined : pos.toUpperCase(), limit: 200 } })
      .then(r => setRows(r.data.players || []))
      .catch(() => setRows([]));
  }, [fmt, pos]);

  const posLabel = POSITIONS[pos];
  const fmtLabel = FORMATS[fmt];
  const title = `Dynasty ${fmtLabel} ${pos === "all" ? "" : posLabel + " "}Rankings ${SEASON}`;
  const canonical = `https://dynastymetrics.com/rankings/${fmt}/${pos}`;
  const desc = `The top dynasty ${fmtLabel} ${pos === "all" ? "players" : posLabel + "s"} for ${SEASON}, ranked by Dynasty Metrics' proprietary DME value — updated daily.`;

  // Structured data: ranked ItemList (rich results + AI citation), breadcrumb, and an FAQ.
  const ld = (rows && rows.length) ? {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ItemList",
        "name": title,
        "description": desc,
        "url": canonical,
        "numberOfItems": rows.length,
        "itemListOrder": "https://schema.org/ItemListOrderDescending",
        "itemListElement": rows.slice(0, 100).map((p, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "url": `https://dynastymetrics.com/players/${p.slug}`,
          "name": `${p.name} (${p.position}${p.team ? " · " + p.team : ""}) — DME ${p.dme}`,
        })),
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dynastymetrics.com/" },
          { "@type": "ListItem", "position": 2, "name": "Dynasty Rankings", "item": "https://dynastymetrics.com/rankings/superflex/all" },
          { "@type": "ListItem", "position": 3, "name": title, "item": canonical },
        ],
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": `Who is the #1 ranked dynasty ${pos === "all" ? "asset" : posLabel} in ${fmtLabel} for ${SEASON}?`,
            "acceptedAnswer": { "@type": "Answer", "text": rows[0] ? `${rows[0].name} (${rows[0].position}${rows[0].team ? ", " + rows[0].team : ""}) is the #1 ranked dynasty ${pos === "all" ? "asset" : posLabel} in ${fmtLabel} with a Dynasty Metrics (DME) value of ${rows[0].dme}.` : "" },
          },
          {
            "@type": "Question",
            "name": "How often are Dynasty Metrics rankings updated?",
            "acceptedAnswer": { "@type": "Answer", "text": "Dynasty Metrics rankings are re-priced automatically every day from live market data, blending crowd sentiment with observed real-trade values and dynasty age curves into the proprietary DME value." },
          },
        ],
      },
    ],
  } : null;

  return (
    <PublicShell>
      <Helmet>
        <title>{`${title} | Dynasty Metrics`}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:url" content={canonical} />
        {ld && <script type="application/ld+json">{JSON.stringify(ld)}</script>}
      </Helmet>

      <div className="space-y-6 fade-up" data-testid="pos-rankings">
        <header>
          <div className="text-[var(--accent)] text-xs tracking-widest mb-1 font-mono">// DYNASTY RANKINGS</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tight" data-testid="pos-rankings-heading">{title}</h1>
          <p className="text-[var(--text-2)] mt-2 max-w-2xl flex flex-wrap items-center gap-1">
            Top dynasty {pos === "all" ? "assets" : posLabel + "s"} ranked by <span className="inline-flex items-center gap-1 text-[var(--accent)]">proprietary DME value <DmeTag /></span> · re-priced daily.
          </p>
        </header>

        {/* Format + position filters (internal links for crawl depth) */}
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            {Object.entries(FORMATS).map(([k, v]) => (
              <Link key={k} to={`/rankings/${k}/${pos}`} data-testid={`rank-fmt-${k}`}
                className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${k === fmt ? "bg-[var(--accent)] text-black font-semibold border-transparent" : "text-[var(--text-2)] border-[var(--border)] hover:text-white"}`}>{v}</Link>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(POSITIONS).map(([k, v]) => (
              <Link key={k} to={`/rankings/${fmt}/${k}`} data-testid={`rank-pos-${k}`}
                className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${k === pos ? "bg-white/10 text-white font-semibold border-[var(--accent)]/40" : "text-[var(--text-2)] border-[var(--border)] hover:text-white"}`}>{v}</Link>
            ))}
          </div>
        </div>

        {rows === null ? (
          <div className="card-bordered p-12 flex justify-center"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div>
        ) : (
          <div className="card-bordered overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] tracking-[0.15em] text-[var(--text-3)] font-mono border-b border-[var(--border)]">
              <div className="col-span-1">#</div>
              <div className="col-span-7">PLAYER</div>
              <div className="col-span-4 text-right inline-flex items-center gap-1.5 justify-end">DME VALUE <DmeTag /></div>
            </div>
            {rows.map((p, i) => (
              <Link key={p.slug} to={`/players/${p.slug}`} className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center text-sm border-b border-[var(--border)] last:border-0 hover:bg-white/5" data-testid={`pos-rank-row-${p.slug}`}>
                <div className="col-span-1 font-mono text-[var(--text-3)]">{i + 1}</div>
                <div className="col-span-7 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate hover:text-[var(--accent)]">{p.name}</span>
                    <PosRank rank={p.pos_rank} />
                  </div>
                  <div className="text-[10px] tracking-widest text-[var(--text-3)]">{p.position}{p.team ? ` · ${p.team}` : ""}{p.age ? ` · ${p.age}y` : ""}</div>
                </div>
                <div className="col-span-4 text-right font-mono text-[var(--accent)]">{formatValue(p.dme)}</div>
              </Link>
            ))}
            {rows.length === 0 && <div className="p-8 text-center text-sm text-[var(--text-3)]">No players found.</div>}
          </div>
        )}

        <div className="card-bordered p-6 text-center" data-testid="pos-rankings-cta">
          <div className="font-display text-xl font-bold mb-2">Get these rankings for your league</div>
          <p className="text-sm text-[var(--text-2)] max-w-md mx-auto mb-4">Sync your Sleeper league for rankings re-priced to your exact scoring, plus a free Trade Calculator and live market trends.</p>
          <Link to="/login?signup=1" className="btn-primary text-sm inline-block" data-testid="pos-rankings-cta-btn">Start free</Link>
        </div>
      </div>
    </PublicShell>
  );
}
